import { getRelationshipLabel } from '@fs/zion-sls'
import { i18n } from '@fs/zion-locale'
import { isSubstring } from '@fs/zion-ui-select'
import { NO_RELATION_TYPE } from '../../shared/relationships/relationshipConstants'

/**
 * @typedef {Object} RelationshipOption
 * @property {string} key - Composite key, e.g. 'PARENT_CHILD_FIRST'.
 * @property {string} primaryText - Localized label.
 */

/**
 * The pinned first row of the picker, and the default selection on every row.
 *
 * Covers both "not related" and "the record does not say": SLS cannot store the difference. Selecting
 * it stores nothing and clears any existing relationship.
 *
 * @returns {RelationshipOption} The pinned option.
 */
export const getNoRelationOption = () => ({
  key: NO_RELATION_TYPE,
  primaryText: i18n.t('relationships-card.no-relation-or-unknown', 'No relation or unknown'),
})

/**
 * @typedef {Object} RelationshipTerm
 * @property {RelationshipOption} option - What the picker shows.
 * @property {Array<string>} searchLabels - Labels the filter matches: the shown one plus every gendered form.
 */

/**
 * Every label one side of a relationship type goes by, so "husband" finds Spouse and "child" finds Son.
 *
 * @param {Object} rel - One SLS relationship type.
 * @param {'first'|'second'} side - Which side of the relationship.
 * @returns {Array<string>} The neutral, male and female labels that are set.
 */
const getSideLabels = (rel, side) =>
  [rel[`${side}DisplayLabel`], rel[`${side}MaleDisplayLabel`], rel[`${side}FemaleDisplayLabel`]].filter(Boolean)

/**
 * Build one member's relationship terms, alphabetised by the label shown.
 *
 * @param {Object} types - SLS types object.
 * @param {Object} [otherPerson] - The member this row is about; their SEX_CODE genders the labels.
 * @returns {Array<RelationshipTerm>} The terms, without the pinned row.
 */
const getRelationshipTerms = (types, otherPerson) => {
  const terms = (types?.relationships ?? []).reduce((acc, rel) => {
    const firstLabel = getRelationshipLabel({ otherPerson, order: 'FIRST', typeData: rel })
    const secondLabel = getRelationshipLabel({ otherPerson, order: 'SECOND', typeData: rel })

    acc.push({
      option: { key: `${rel.relType}_FIRST`, primaryText: firstLabel },
      searchLabels: [firstLabel, ...getSideLabels(rel, 'first')],
    })
    if (firstLabel !== secondLabel) {
      acc.push({
        option: { key: `${rel.relType}_SECOND`, primaryText: secondLabel },
        searchLabels: [secondLabel, ...getSideLabels(rel, 'second')],
      })
    }
    return acc
  }, [])

  return terms.sort((a, b) => a.option.primaryText.localeCompare(b.option.primaryText))
}

/**
 * Build the picker list for one group member: "No relation or unknown" pinned, then everything
 * else alphabetised with no group headings.
 *
 * Labels are gendered by `otherPerson` to match the seeded value, which resolves Father over Parent;
 * an ungendered list would omit the value the row opens on. No recorded sex falls back to generic.
 *
 * @param {Object} types - SLS types object.
 * @param {Object} [otherPerson] - The member this row is about; their SEX_CODE genders the labels.
 * @returns {Array<RelationshipOption>} Pinned row, then the alphabetised terms.
 */
export const getRelationshipOptions = (types, otherPerson) => [
  getNoRelationOption(),
  ...getRelationshipTerms(types, otherPerson).map(({ option }) => option),
]

/**
 * Build the `getOptions` callback for one member's relationship FilterSelect.
 *
 * FilterSelect does not match for you. Matching is substring rather than prefix, so "child"
 * returns Adopted child, Foster child, Godchild, Stepchild and the rest. Each option also answers
 * to its other genders, so "child" finds Son and "husband" finds Spouse.
 *
 * @param {Object} types - SLS types object.
 * @param {Object} [otherPerson] - The member this row is about; their SEX_CODE genders the labels.
 * @returns {(inputValue: string) => Array<RelationshipOption>} The picker's `getOptions`.
 */
export const createRelationshipOptionGetter = (types, otherPerson) => {
  const noRelationOption = getNoRelationOption()
  const terms = [
    { option: noRelationOption, searchLabels: [noRelationOption.primaryText] },
    ...getRelationshipTerms(types, otherPerson),
  ]
  const options = terms.map(({ option }) => option)

  return (inputValue) => {
    if (!inputValue) return options
    return terms
      .filter(({ searchLabels }) => searchLabels.some((label) => isSubstring(inputValue, label)))
      .map(({ option }) => option)
  }
}
